import { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'transmute/debounce';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import PortalIdParser from 'PortalIdParser';
import formatName from 'I18n/utils/formatName';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H2 from 'UIComponents/elements/headings/H2';
import Small from 'UIComponents/elements/Small';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UILink from 'UIComponents/link/UILink';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIModal from 'UIComponents/dialog/UIModal';
import UISelect from 'UIComponents/input/UISelect';
import ValidationMessageToField from './ValidationMessageToField';
import ValidationMessageCCField from './ValidationMessageCCField';
import ValidationMessageFromField from './ValidationMessageFromField';
import virtualizedMenuRenderer from 'UIComponents/input/utils/virtualizedMenuRenderer';
import { getEmailOptionDisplayValue, getFromValuesGroupedSelectOptions } from '../../utils/invoiceEmail';
import { getInvoiceAssociatedObjectProperty } from '../../utils/property';
import { isValidEmail } from '../../utils/validation';
import { fetchEmailContactOptions } from '../../api/CrmSearchAPI';
import useFromValues from '../../hooks/useFromValues';
import useOnSubmitHandler from '../../hooks/useOnSubmitHandler';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const rateLimitExceededErrorStatus = 429;
const DEBOUNCE_DELAY = 300;
const EMAIL_SETTINGS_URL = `/settings/${PortalIdParser.get()}/user-preferences/email`;
const MAXIMUM_SELECTED_CONTACTS = 10;
const unmapContacts = contacts => contacts.reduce((knownContacts, contact) => {
  const key = contact.objectId === null ? contact.properties.email.value : contact.objectId;
  return Object.assign({}, knownContacts, {
    [key]: contact
  });
}, {});
export const getOptionsFromContacts = (contacts, optionDisplayNameFormatter = getEmailOptionDisplayValue) => contacts.map(contact => {
  const email = contact.properties.email && contact.properties.email.value;
  const name = formatName({
    firstName: getInvoiceAssociatedObjectProperty(contact, 'firstname'),
    lastName: getInvoiceAssociatedObjectProperty(contact, 'lastname')
  });
  const text = optionDisplayNameFormatter({
    name,
    email
  });
  const value = contact.objectId || email;
  const shouldShowWarning = !email || !isValidEmail(email);
  return {
    text,
    value,
    tagUse: shouldShowWarning ? 'danger' : undefined,
    icon: shouldShowWarning ? 'warning' : undefined
  };
});
export const getOptions = (input, contacts, optionDisplayNameFormatter) => {
  const sanitizedInput = input.trim();
  const doesInputMatchContact = Boolean(contacts.find(contact => contact.properties.email && contact.properties.email.value.toLowerCase() === sanitizedInput));
  const options = getOptionsFromContacts(contacts, optionDisplayNameFormatter);
  const shouldShowWarning = !isValidEmail(sanitizedInput);
  if (doesInputMatchContact) {
    return options;
  } else {
    return [...options, {
      text: sanitizedInput,
      value: sanitizedInput,
      tagUse: shouldShowWarning ? 'danger' : undefined,
      icon: shouldShowWarning ? 'warning' : undefined
    }];
  }
};

// We do not want both of the debounce handlers below to use the same
// function reference, as it produces strange UI bugs. We instead
// use this function to generate separate references.
const getHandleLoadOptions = (query, setter) => (input, callback) => {
  if (!input.length) {
    callback(null);
    return;
  }
  query(input).then(contacts => {
    // This allows us to keep a running record of all 'seen' contact objects, as well as
    // 'new' contacts (created in the UISelect if the input doesn't match an actual record).
    // The UISelect sets as its value the objectId or user input, and we use the persisted set
    // of seen contact objects to grab the full object details (properties, etc.)
    // for display purposes and to send in the submit handler.
    setter(previousContacts => Object.assign({}, previousContacts, unmapContacts(contacts)));
    callback(null, {
      options: getOptions(input, contacts)
    });
  })
  // This would be a failure during the user input-triggered search. On failure,
  // swallow the error quietly so the user can hopefully recover by continuing to type.
  .catch(() => {});
};
const getNewContact = value => ({
  objectId: null,
  objectTypeId: CONTACT_TYPE_ID,
  properties: {
    email: {
      name: 'email',
      value
    }
  },
  portalId: PortalIdParser.get()
});
const getValue = contact => contact.objectId || contact.properties.email.value;
export default function InvoiceSendEmailModal({
  invoice,
  onClose,
  onInteraction,
  onUsage
}) {
  const onCloseWithSuccess = useCallback(() => onClose({
    success: true
  }), [onClose]);
  const onCloseWithError = useCallback(({
    error,
    fatalError
  }) => onClose({
    error,
    fatalError
  }), [onClose]);
  const onCloseWithNoResponse = useCallback(() => onClose({}), [onClose]);
  const contacts = invoice.associatedObjects.INVOICE_TO_CONTACT;
  const [knownContacts, setKnownContacts] = useState(unmapContacts(contacts));
  const [selectedToContacts, setSelectedToContacts] = useState(contacts.slice(0, 1));
  const [selectedCcContacts, setSelectedCcContacts] = useState(contacts.slice(1));
  const {
    isLoading: isLoadingFromValues,
    fromValues,
    selectedFromValue,
    setSelectedFromValue,
    allowOtherEmails,
    defaultEmail
  } = useFromValues(onCloseWithError);
  const {
    onSubmitSendRequest,
    onSubmitWriteEmailRequest,
    isLoading: isLoadingSubmitButton
  } = useOnSubmitHandler({
    invoice,
    from: selectedFromValue,
    toContacts: selectedToContacts,
    ccContacts: selectedCcContacts,
    onSuccess: onCloseWithSuccess,
    onError: onCloseWithError
  });
  const handleToOnChange = event => {
    const value = event.target.value;
    const selectedContact = knownContacts[value] || getNewContact(value);
    setSelectedToContacts([selectedContact]);
    setKnownContacts(previousKnownContacts => Object.assign({}, previousKnownContacts, unmapContacts([selectedContact])));
    onInteraction({
      action: 'Set TO contact'
    });
  };
  const handleCcOnChange = event => {
    const selectedContacts = event.target.value.map(value => knownContacts[value] || getNewContact(value));
    setSelectedCcContacts(selectedContacts);
    setKnownContacts(previousKnownContacts => Object.assign({}, previousKnownContacts, unmapContacts(selectedContacts)));
    onInteraction({
      action: selectedContacts.length > selectedCcContacts.length ? 'Add CC contact' : 'Remove CC contact'
    });
  };
  const handleFromOnChange = event => {
    const foundFromValue = fromValues.find(fromValue => fromValue.email === event.target.value);
    if (foundFromValue === undefined) {
      onCloseWithError({
        fatalError: 'No matching From option found.'
      });
      return;
    }
    setSelectedFromValue(foundFromValue);
    onInteraction({
      action: 'Set From value'
    });
  };
  const debouncedHandleToLoadOptions = useRef(debounce(DEBOUNCE_DELAY, getHandleLoadOptions(fetchEmailContactOptions, setKnownContacts))).current;
  const debouncedHandleCcLoadOptions = useRef(debounce(DEBOUNCE_DELAY, getHandleLoadOptions(fetchEmailContactOptions, setKnownContacts))).current;
  const validationErrors = useMemo(() => ({
    tooManyContacts: selectedCcContacts.length + selectedToContacts.length > MAXIMUM_SELECTED_CONTACTS,
    to: {
      invalidEmails: !selectedToContacts.every(contact => isValidEmail(getInvoiceAssociatedObjectProperty(contact, 'email'))),
      empty: !selectedToContacts.length
    },
    cc: {
      invalidEmails: !selectedCcContacts.every(contact => isValidEmail(getInvoiceAssociatedObjectProperty(contact, 'email')))
    },
    from: {
      defaultEmailDisabled: !!selectedFromValue && selectedFromValue.disabled && selectedFromValue.accountId === defaultEmail,
      empty: !isLoadingFromValues && fromValues.length === 0,
      userEmailDisabled: !!selectedFromValue && selectedFromValue.disabled && !selectedFromValue.shared,
      teamEmailDisabled: !!selectedFromValue && selectedFromValue.disabled && selectedFromValue.shared
    }
  }), [fromValues, isLoadingFromValues, selectedCcContacts, selectedToContacts, selectedFromValue, defaultEmail]);
  const ccFieldError = validationErrors.tooManyContacts || validationErrors.cc.invalidEmails;
  const toFieldError = validationErrors.to.empty || validationErrors.to.invalidEmails || validationErrors.tooManyContacts;
  const fromFieldError = !isLoadingFromValues && (validationErrors.from.empty || validationErrors.from.userEmailDisabled || validationErrors.from.teamEmailDisabled || validationErrors.from.defaultEmailDisabled);
  const isSendButtonEnabled = !isLoadingFromValues && !ccFieldError && !toFieldError && !fromFieldError;
  const groupedFromEmails = useMemo(() => getFromValuesGroupedSelectOptions(fromValues), [fromValues]);
  return /*#__PURE__*/_jsxs(UIModal, {
    "data-test-id": "invoices-send-email-modal",
    width: 574,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.sendInvoiceModal.header"
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        "data-test-id": "invoices-send-email-modal-close",
        onClick: onCloseWithNoResponse
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      className: "p-bottom-6",
      children: [/*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.sendInvoiceModal.fields.to.label"
        }),
        error: toFieldError,
        validationMessage: /*#__PURE__*/_jsx(ValidationMessageToField, {
          validationErrors: validationErrors
        }),
        children: /*#__PURE__*/_jsx(UISelect, {
          allowCreate: true,
          className: "invoice-send-email-field-to",
          onChange: handleToOnChange,
          loadOptions: debouncedHandleToLoadOptions,
          value: selectedToContacts.length > 0 ? getValue(selectedToContacts[0]) : undefined,
          options: getOptionsFromContacts(selectedToContacts)
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.sendInvoiceModal.fields.cc.label"
        }),
        error: ccFieldError,
        validationMessage: /*#__PURE__*/_jsx(ValidationMessageCCField, {
          validationErrors: validationErrors
        }),
        children: /*#__PURE__*/_jsx(UISelect, {
          className: "invoice-send-email-field-cc",
          multi: true,
          onChange: handleCcOnChange,
          loadOptions: debouncedHandleCcLoadOptions,
          value: selectedCcContacts.map(getValue),
          options: getOptionsFromContacts(selectedCcContacts)
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.sendInvoiceModal.fields.from.label"
        }),
        error: fromFieldError,
        validationMessage: selectedFromValue ? /*#__PURE__*/_jsx(ValidationMessageFromField, {
          validationErrors: validationErrors,
          email: selectedFromValue.email,
          allowOtherEmails: allowOtherEmails
        }) : undefined,
        labelTooltip: !allowOtherEmails && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.sendInvoiceModal.fields.from.allowOtherEmailsDisabledTooltip"
        }),
        children: /*#__PURE__*/_jsx(UISelect, {
          "data-test-id": "invoice-send-email-field-from",
          onChange: handleFromOnChange,
          isLoading: isLoadingFromValues,
          options: groupedFromEmails,
          value: !selectedFromValue ? undefined : selectedFromValue.email,
          disabled: isLoadingFromValues,
          readOnly: !allowOtherEmails,
          dropdownFooter: /*#__PURE__*/_jsx(UILink, {
            href: EMAIL_SETTINGS_URL,
            external: true,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "invoices.components.sendInvoiceModal.fields.from.helper"
            })
          }),
          menuRenderer: virtualizedMenuRenderer
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(Small, {
        className: "m-top-8",
        tagName: "p",
        children: /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: "https://knowledge.hubspot.com/payments/create-and-manage-invoices#send",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoices.components.sendInvoiceModal.helper"
          })
        })
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          "data-test-id": "send-modal-send-button",
          use: "primary",
          responsive: false,
          disabled: !isSendButtonEnabled,
          onClick: () => {
            onUsage({
              action: 'Send invoice',
              numberOfToContacts: selectedToContacts.length,
              numberOfCcContacts: selectedCcContacts.length,
              isDefault: !!selectedFromValue && selectedFromValue.accountId === defaultEmail
            });
            onSubmitSendRequest();
          },
          loading: isLoadingSubmitButton,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoices.components.sendInvoiceModal.buttons.send.text"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "send-modal-write-button",
          use: "secondary",
          responsive: false,
          onClick: () => {
            onUsage({
              action: 'Write invoice manually'
            });
            onSubmitWriteEmailRequest();
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoices.components.sendInvoiceModal.buttons.write.text"
          })
        })]
      })]
    })]
  });
}